import React, { ReactNode } from "react";
import { Provider } from "react-redux";
import {
    BrowserRouter as Router,
    Navigate,
    Route,
    Routes,
} from "react-router-dom";
import { Experimental_CssVarsProvider as CssVarsProvider } from "@mui/material/styles";
import { theme } from "../framework/theme/theme";
import { store } from "../framework/redux/store";
import { PrivateRoute } from "../framework/routing/privateRoute";
import { I18nextProvider } from "react-i18next";
import i18n from "../i18n/i18n";
import { LoginPage } from "../components/login/loginPage";
import { Header } from "./header";
import { Main } from "./main";
import { MuiSnackBar } from "./muiSnackBar";
import appConfig from "./appConfig";
import { WorklogMain } from "../components/worklog/worklogMain";
import { DailyWorklogMain } from "../components/worklog/dailyWorklogMain";
import CssBaseline from "@mui/material/CssBaseline";
import { WorkTimeStart } from "../components/worktime/workTimeStart";
import { WorkTimeFormMain } from "../components/workTimeForm/workTimeFormMain";
import { Confirm, ConfirmContextProvider } from "../hooks/framework/useConfirm";
import { WorkTimeFormTabs } from "../components/workTimeForm/workTimeFormTabs";
import { DayBookingFormMain } from "../components/dayBooking/dayBookingFormMain";
import { Profile } from "../components/settings/profile";
import { EmployeeVehicleSelect } from "../components/settings/employeeVehicleSelect";
import { EmployeeCostCenterSelect } from "../components/settings/employeeCostCenterSelect";
import { SettingTabs } from "../components/settings/settingTabs";
import { SignalRListener } from "./signalRListener";
import { AppUpdateHandler } from "./appUpdateHandler";
import { Language } from "../components/settings/language";
import { TransportBase } from "../components/transport/transportBase";
import { TransportOrder } from "../components/transport/transportOrder/transportOrder";
import { Notifications } from "../components/notifications/notifications";
import { useAppSelector } from "../framework/redux/hooks";
import { useNotificationPolling } from "../hooks/notification/useNotificationPolling";
import { useAppOptionValueEquals } from "../hooks/framework/useAppOptionValue";
import { optionOwnerHasTransportNotificationsEnabled } from "../models/common/consts";
import { useAsyncThunkAction } from "../hooks/framework/useAsyncThunkAction";
import { fetchVehicles } from "../framework/redux/slices/vehicleSlice";
import { handleApiError } from "../services/baseService";
import { WorkTimeBreakForm } from "../components/workTimeForm/workTimeBreakForm";
import { WorkTimeDefinitionForm } from "../components/workTimeForm/workTimeDefinitionForm";
import { TransportOrdersQueue } from "../components/transport/transportOrdersQueue";
import { OrderFormMain } from "../components/transport/transportOrder/orderFormMain";

export interface AppProps {
    componentToTest?: ReactNode;
}

const NotificationPollingWrapper = () => {
    const employeeId = useAppSelector((state) => state.session.id);
    useNotificationPolling(employeeId);
    return null;
};

const App = ({ componentToTest }: AppProps) => {
    // Solely for tests; render tested component inside BrowserRouter
    if (componentToTest)
        return <Router>{componentToTest}</Router>;

    const [orderNotificationsEnabled] = useAppOptionValueEquals(
        optionOwnerHasTransportNotificationsEnabled,
        true
    );

    const employeeId = useAppSelector((state) => state.session.id);

    useAsyncThunkAction(
        () => {
            if (employeeId) {
                return fetchVehicles();
            }
        },
        { onError: handleApiError },
        [employeeId]
    );

    return (
        <>
            <Router basename={appConfig.basename}>
                {orderNotificationsEnabled && (
                    <NotificationPollingWrapper />
                )}
                <Routes>
                    <Route path="*" element={<AppUpdateHandler />} />
                </Routes>
                <Routes>
                    <Route path="*" element={<SignalRListener />} />
                </Routes>
                <Routes>
                    <Route path="/login" element={null} />
                    <Route path="/*" element={<Header />} />
                </Routes>
                <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route
                        path="/"
                        element={
                            <PrivateRoute>
                                <Main />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/settings/*"
                        element={
                            <PrivateRoute>
                                <SettingTabs />
                            </PrivateRoute>
                        }
                    >
                        <Route
                            path="vehicles"
                            element={<EmployeeVehicleSelect />}
                        />
                        <Route
                            path="costCenters"
                            element={<EmployeeCostCenterSelect />}
                        />
                        <Route path="profile" element={<Profile />} />
                        <Route path="language" element={<Language />} />
                    </Route>
                    <Route
                        path="/work/form/*"
                        element={
                            <PrivateRoute>
                                <WorkTimeFormTabs />
                            </PrivateRoute>
                        }
                    >
                        <Route path="daybooking/*">
                            <Route
                                path="edit/:id/:date?"
                                element={<DayBookingFormMain />}
                            />
                            <Route
                                path="new/:date?"
                                element={<DayBookingFormMain />}
                            />
                        </Route>
                        <Route path="other/*">
                            <Route
                                path="edit/:id/:date?"
                                element={<WorkTimeFormMain isCustom />}
                            />
                            <Route
                                path="new/:date?"
                                element={<WorkTimeFormMain isCustom />}
                            />
                        </Route>
                        <Route
                            path="edit/:id/:date?"
                            element={<WorkTimeFormMain />}
                        />
                        <Route
                            path="new/:date?"
                            element={<WorkTimeFormMain />}
                        />
                    </Route>
                    <Route
                        path="/work/break/:date?"
                        element={
                            <PrivateRoute>
                                <WorkTimeBreakForm />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/work/workstage/definition/:workTimeTypeId"
                        element={
                            <PrivateRoute>
                                <WorkTimeDefinitionForm />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/work/start"
                        element={
                            <PrivateRoute>
                                <WorkTimeStart />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/worklog/calendar/*"
                        element={
                            <PrivateRoute>
                                <WorklogMain />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/worklog/:date?"
                        element={
                            <PrivateRoute>
                                <DailyWorklogMain />
                            </PrivateRoute>
                        }
                    />
                    <>
                        <Route
                            path="/transport/*"
                            element={
                                <PrivateRoute>
                                    <TransportBase />
                                </PrivateRoute>
                            }
                        >
                            <Route
                                path="orders"
                                element={<TransportOrdersQueue />}
                            />
                            <Route
                                path="order/:orderId"
                                element={<TransportOrder />}
                            />
                            <Route
                                path="order/new"
                                element={<OrderFormMain />}
                            />
                            <Route
                                path="order/edit/:orderId"
                                element={<OrderFormMain />}
                            />
                        </Route>
                        <Route
                            path="/notifications"
                            element={
                                <PrivateRoute>
                                    <Notifications />
                                </PrivateRoute>
                            }
                        />
                    </>
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </Router>
            <MuiSnackBar />
        </>
    );
};

export interface AppConnectedProps {
    storeProps?: any;
    componentToTest?: ReactNode;
}

const AppConnected = ({ storeProps, componentToTest }: AppConnectedProps) => {
    return (
        <Provider store={storeProps ?? store}>
            <CssVarsProvider theme={theme}>
                <CssBaseline />
                <I18nextProvider i18n={i18n}>
                    <ConfirmContextProvider>
                        <App componentToTest={componentToTest} />
                        <Confirm />
                    </ConfirmContextProvider>
                </I18nextProvider>
            </CssVarsProvider>
        </Provider>
    );
};

export default AppConnected;
